var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sentry } from 'app/utils/sentry';
import { isWebHidSupportedInBrowser } from '../isWebHidSupportedInBrowser';
import { bufferPositions, busylightDeviceFilters, busylightStatus, tones, } from './busylightConfigs';
/** Manages Busylight devices using WebHID API. */
export class BusylightDeviceManager {
    /** Gets the current buffer cache. */
    get currentBufferCache() {
        return this.bufferCache;
    }
    /** Checks if there are connected devices. */
    get hasConnectedDevices() {
        return this.devices.size > 0;
    }
    /** Gets the connected HID devices. */
    get connectedDevices() {
        return Array.from(this.devices.values());
    }
    constructor({ searchInterval = 20000, keepAlive = false, } = {}) {
        /** Set of connected HID devices. */
        this.devices = new Set();
        /** Event listeners for Busylight events. */
        this.listeners = {
            deviceAdded: new Set(),
            deviceRemoved: new Set(),
            arrayBufferChange: new Set(),
        };
        if (!isWebHidSupportedInBrowser()) {
            return;
        }
        this.keepAlive = keepAlive;
        this.searchForDevices(searchInterval);
    }
    /** Recursively searches for connected Busylight devices. */
    searchForDevices(searchInterval) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.searchTimeoutId)
                clearTimeout(this.searchTimeoutId);
            yield this.updateDevices();
            this.searchTimeoutId = setTimeout(() => {
                this.searchForDevices(searchInterval);
            }, searchInterval);
        });
    }
    /** Updates the list of connected devices. */
    updateDevices() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const devices = yield navigator.hid.getDevices();
                const busylightDevices = devices.filter((device) => busylightDeviceFilters.supportedKuandoDevices.find((filter) => filter.vendorId === device.vendorId));
                this.addOrRemoveDevices(busylightDevices);
            }
            catch (err) {
                Sentry.captureException(err);
            }
        });
    }
    /** Triggers a request to connect Busylight devices. */
    triggerRequestDevice() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const devices = yield navigator.hid.requestDevice({
                    filters: busylightDeviceFilters.supportedKuandoDevices,
                });
                if (!devices.length)
                    return;
                this.addOrRemoveDevices(devices);
            }
            catch (err) {
                Sentry.captureException(err);
            }
        });
    }
    /** Adds or removes devices based on the provided list. */
    addOrRemoveDevices(devices) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const device of this.devices) {
                if (!devices.includes(device)) {
                    try {
                        if (device.opened) {
                            yield device.close();
                        }
                        this.removeDevice(device);
                    }
                    catch (err) {
                        Sentry.captureException(err);
                    }
                }
            }
            for (const device of devices) {
                if (!this.devices.has(device)) {
                    try {
                        if (!device.opened) {
                            yield device.open();
                        }
                        this.addDevice(device);
                    }
                    catch (err) {
                        Sentry.captureException(err);
                    }
                }
            }
        });
    }
    /** Adds a device to the set of connected devices. */
    addDevice(device) {
        this.devices.add(device);
        this.emit('deviceAdded', device);
    }
    /** Removes a device from the set of connected devices. */
    removeDevice(device) {
        this.devices.delete(device);
        this.emit('deviceRemoved', device);
    }
    /** Creates an array buffer based on the provided options. */
    createArrayBuffer({ color, tone = 'TelephoneNordic', volume = 0, onTimeSteps = 0, offTimeSteps = 0, repeatInterval = 120, }) {
        var _a, _b, _c;
        const arrayBuffer = new Uint8Array(64);
        arrayBuffer[0] = 16;
        arrayBuffer.fill(0, 1); // Set all elements from index 1 to the end to zero
        arrayBuffer.fill(255, 59, 62); // Set elements from index 59 to 61 to 255
        // Color
        arrayBuffer[bufferPositions.red] = (_a = color === null || color === void 0 ? void 0 : color.r) !== null && _a !== void 0 ? _a : 0;
        arrayBuffer[bufferPositions.green] = (_b = color === null || color === void 0 ? void 0 : color.g) !== null && _b !== void 0 ? _b : 0;
        arrayBuffer[bufferPositions.blue] = (_c = color === null || color === void 0 ? void 0 : color.b) !== null && _c !== void 0 ? _c : 0;
        // Sound
        arrayBuffer[bufferPositions.sound] = tones[tone] + volume;
        // On / Off time steps
        arrayBuffer[bufferPositions.onTimeSteps] = onTimeSteps;
        arrayBuffer[bufferPositions.offTimeSteps] = offTimeSteps;
        arrayBuffer[bufferPositions.repeatInterval] = repeatInterval;
        let checksum = 0;
        for (let i = 0; i < 62; i++) {
            checksum += arrayBuffer[i];
        }
        arrayBuffer[62] = checksum / 256;
        arrayBuffer[63] = checksum % 256;
        return arrayBuffer;
    }
    /** Sends a command to the connected devices. */
    send(options) {
        if (!this.devices.size)
            return;
        this.bufferCache = this.createArrayBuffer(options);
        this.emit('arrayBufferChange', this.bufferCache);
        this.sendToDevices(this.bufferCache);
    }
    /** Sets the status of the Busylight devices. */
    setStatus(name, overrides) {
        this.send(Object.assign(Object.assign({}, busylightStatus[name]), (typeof overrides !== 'undefined' && overrides)));
    }
    /** Sends the array buffer to the connected devices. */
    sendToDevices(arrayBuffer) {
        for (const device of this.devices) {
            try {
                device.sendReport(0, arrayBuffer);
            }
            catch (err) {
                // Don't do anything, this can happen if the devices have been removed
            }
        }
    }
    /** Updates the devices with the provided array buffer. */
    update(arrayBuffer) {
        this.sendToDevices(arrayBuffer);
        if (this.keepAlive) {
            if (this.keepAliveTimeoutId) {
                clearTimeout(this.keepAliveTimeoutId);
            }
            this.keepAliveTimeoutId = setTimeout(() => {
                this.update(arrayBuffer);
            }, 10000);
        }
    }
    /** Emits an event with the provided arguments. */
    emit(key, arg) {
        for (const handler of this.listeners[key]) {
            handler(arg);
        }
    }
    /** Adds an event handler for the specified event. */
    on(key, handler) {
        this.listeners[key].add(handler);
    }
    /** Removes an event handler for the specified event. */
    remove(key, handler) {
        this.listeners[key].delete(handler);
    }
}
const isSharedWorkerSupported = 'SharedWorker' in window;
export const busylightDeviceManager = new BusylightDeviceManager({
    keepAlive: !isSharedWorkerSupported,
});
